import { useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";

export type OrderHandoverRosterViewState = {
  keyword: string; type: string; classGroup: string; stopped: string;
  page: number; pageSize: number; customerQuoteId: string;
  windowScrollTop: number; contentScrollTop: number; tableScrollTop: number; tableScrollLeft: number;
};

export type OrderHandoverRosterReturnState = OrderHandoverRosterViewState & {
  projectId: string; rosterId: string; search: string;
};

/** 只接受当前项目名单的返回状态，防止其他页面或旧名单状态串用。 */
export function readOrderHandoverRosterReturnState(state: unknown, projectId: string, rosterId?: string): OrderHandoverRosterReturnState | undefined {
  if (!state || typeof state !== "object" || !("orderHandoverRosterReturn" in state)) return undefined;
  const value = state.orderHandoverRosterReturn;
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<OrderHandoverRosterReturnState>;
  if (candidate.projectId !== projectId || (rosterId && candidate.rosterId !== rosterId)) return undefined;
  const textKeys = ["rosterId", "search", "keyword", "type", "classGroup", "stopped", "customerQuoteId"] as const;
  if (textKeys.some((key) => typeof candidate[key] !== "string")) return undefined;
  const countKeys = ["page", "pageSize", "windowScrollTop", "contentScrollTop", "tableScrollTop", "tableScrollLeft"] as const;
  if (countKeys.some((key) => typeof candidate[key] !== "number" || !Number.isFinite(candidate[key]) || candidate[key]! < 0)) return undefined;
  if (!Number.isInteger(candidate.page) || candidate.page! < 1 || ![10, 20, 50, 100].includes(candidate.pageSize!)) return undefined;
  return candidate as OrderHandoverRosterReturnState;
}

/** 名单数据和分页已落地后恢复滚动；不写浏览器持久存储。 */
export function useOrderHandoverRosterReturn(
  saved: OrderHandoverRosterReturnState | undefined,
  rosterId: string | undefined,
  loading: boolean,
  rootRef: RefObject<HTMLElement | null>,
) {
  const restored = useRef(false);
  useLayoutEffect(() => {
    if (!saved || restored.current || loading || saved.rosterId !== rosterId || !rootRef.current) return;
    const root = rootRef.current;
    const restore = () => {
      const content = root.closest<HTMLElement>("[data-testid='admin-content-shell']");
      if (content) content.scrollTop = saved.contentScrollTop;
      const table = root.querySelector<HTMLElement>(".order-handover-roster-table-wrap .arco-table-body");
      if (table) { table.scrollTop = saved.tableScrollTop; table.scrollLeft = saved.tableScrollLeft; }
      if (window.scrollY !== saved.windowScrollTop) window.scrollTo({ top: saved.windowScrollTop });
      restored.current = true;
    };
    const frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [saved, rosterId, loading, rootRef]);
}
